import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Phone, Settings } from "lucide-react";
import { useAutoSave } from "@/hooks/useAutoSave";
import { VoiceAgent } from "@/services/voiceAgentService";
import { Slider } from "@/components/ui/slider";
import SectionHeader from "@/components/agent/shared/SectionHeader";

const callSettingsSchema = z.object({
  first_message: z.string().optional(),
  end_call_message: z.string().optional(),
  transfer_phone_number: z.string().optional(),
  max_duration_seconds: z.number().optional().nullable(),
  voicemail_message: z.string().optional(),
  ai_temperature: z.number().min(0).max(2).default(0.7),
});

type CallSettingsFormValues = z.infer<typeof callSettingsSchema>;

interface VoiceAgentCallSettingsFormProps {
  agent: VoiceAgent;
  updateAgent: (data: Partial<VoiceAgent>) => Promise<boolean>;
  showOnly?: string[];
}

const VoiceAgentCallSettingsForm = ({
  agent,
  updateAgent,
  showOnly,
}: VoiceAgentCallSettingsFormProps) => {
  const [initialFormValues, setInitialFormValues] =
    useState<CallSettingsFormValues | null>({
      first_message: agent.first_message || "",
      end_call_message: agent.end_call_message || "",
      transfer_phone_number: agent.transfer_phone_number || "",
      max_duration_seconds: agent.max_duration_seconds || null,
      voicemail_message: agent.voicemail_message || "",
      ai_temperature: agent.ai_temperature || 0.7,
    });

  const form = useForm<CallSettingsFormValues>({
    resolver: zodResolver(callSettingsSchema),
    defaultValues: {
      first_message: agent.first_message || "",
      end_call_message: agent.end_call_message || "",
      transfer_phone_number: agent.transfer_phone_number || "",
      max_duration_seconds: agent.max_duration_seconds || null,
      voicemail_message: agent.voicemail_message || "",
      ai_temperature: agent.ai_temperature || 0.7,
    },
  });

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!initialFormValues) return;
      const data = form.getValues();
      return await updateAgent(data);
    },
    successMessage: "Call settings saved automatically",
    errorMessage: "Failed to auto-save call settings",
  });

  // Update form when agent data changes
  useEffect(() => {
    const newFormValues = {
      first_message: agent.first_message || "",
      end_call_message: agent.end_call_message || "",
      transfer_phone_number: agent.transfer_phone_number || "",
      max_duration_seconds: agent.max_duration_seconds || null,
      voicemail_message: agent.voicemail_message || "",
      ai_temperature: agent.ai_temperature || 0.7,
    };

    form.reset(newFormValues);
    setInitialFormValues(newFormValues);
  }, [
    agent.id,
    agent.first_message,
    agent.end_call_message,
    agent.transfer_phone_number,
    agent.max_duration_seconds,
    agent.voicemail_message,
    agent.ai_temperature,
    form,
  ]);

  // Watch for changes
  useEffect(() => {
    if (!initialFormValues) return;
    const handleChange = () => {
      const data = form.getValues();
      const hasChanges = Object.keys(data).some((key) => {
        return (
          data[key as keyof CallSettingsFormValues] !==
          initialFormValues[key as keyof CallSettingsFormValues]
        );
      });
      if (hasChanges) {
        setDirty();
        setInitialFormValues(data);
      }
    };
    const subscription = form.watch(handleChange);
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  const shouldShowField = (fieldName: string) =>
    !showOnly || showOnly.includes(fieldName);

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Pre-set Messages Block */}
        <Card className="border rounded-2xl">
          <CardContent className="p-5">
            <SectionHeader
              icon={<MessageSquare className="h-5 w-5" />}
              title="Pre-set Messages"
            />

            <div className="space-y-4">
              {/* First Message */}
              {shouldShowField("first_message") && (
                <FormField
                  control={form.control}
                  name="first_message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        First Message
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What should the agent say when the call starts?"
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* End Call Message */}
              {shouldShowField("end_call_message") && (
                <FormField
                  control={form.control}
                  name="end_call_message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        End Call Message
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What should the agent say before ending the call?"
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Call Forwarding Block */}
        <Card className="border rounded-2xl">
          <CardContent className="p-5">
            <SectionHeader
              icon={<Phone className="h-5 w-5" />}
              title="Call Forwarding"
            />

            <div className="space-y-4">
              {/* Transfer Phone Number */}
              {shouldShowField("transfer_phone_number") && (
                <FormField
                  control={form.control}
                  name="transfer_phone_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Transfer Phone Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="e.g., +1234567890"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-1">
                        Phone number to transfer calls to (optional). If set,
                        adds a transfer call tool to the agent.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Advanced Block */}
        <Card className="border rounded-2xl">
          <CardContent className="p-5">
            <SectionHeader
              icon={<Settings className="h-5 w-5" />}
              title="Advanced"
            />

            <div className="space-y-4">
              {/* Maximum Call Duration */}
              {shouldShowField("max_duration_seconds") && (
                <FormField
                  control={form.control}
                  name="max_duration_seconds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Maximum Call Duration (seconds)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="e.g., 1800 (30 minutes)"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? parseInt(value) : null);
                          }}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty for no limit
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Voicemail Message */}
              {shouldShowField("voicemail_message") && (
                <FormField
                  control={form.control}
                  name="voicemail_message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Voicemail Message
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What should the agent say when leaving a voicemail?"
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* AI Temperature */}
              {shouldShowField("ai_temperature") && (
                <FormField
                  control={form.control}
                  name="ai_temperature"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        AI Temperature
                      </FormLabel>
                      <FormControl>
                        <div className="px-3">
                          <Slider
                            min={0}
                            max={2}
                            step={0.1}
                            value={[field.value]}
                            onValueChange={(value) => field.onChange(value[0])}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Conservative (0)</span>
                            <span className="font-medium">
                              {field.value.toFixed(1)}
                            </span>
                            <span>Creative (2)</span>
                          </div>
                        </div>
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-1">
                        Controls the AI's creativity. Lower values make
                        responses more predictable.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Auto-save indicator */}
        {isSaving && (
          <div className="flex justify-end">
            <span className="text-sm text-muted-foreground">Auto-saving…</span>
          </div>
        )}
      </div>
    </Form>
  );
};

export default VoiceAgentCallSettingsForm;
